
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  FileText,
  IndianRupee,
  MapPin,
  Plus,
  Send,
  Users,
  X,
  CreditCard,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

import jobService from "../../services/JobService";
import {
  getActiveSubscription,
  getActiveFeatureUsage,
} from "../../services/subscriptionProductService";

import PartnerFooter from "../../component/PartnerFooter";
import PartnerHeader from "../../component/PartnerHeader";

const API_PRIMARY = "#3E0F8D";
const API_SECONDARY = "#9564DD";

const INITIAL_JOB = {
  jobTitle: "",
  jobDescription: "",
  location: "",
  employeeType: "",
  experienceRequired: "",
  salaryMin: "",
  salaryMax: "",
  skillsRequired: "",
  vacancy: "",
  applicationDeadline: "",
};

// =====================================================
// USER HELPERS
// =====================================================

const getLoggedInUser = () => {
  try {
    const raw = localStorage.getItem("user");

    if (raw) {
      const user = JSON.parse(raw);

      return {
        ...user,
        id:
          user?.id ??
          user?.userId ??
          user?.user_id ??
          localStorage.getItem("userId") ??
          localStorage.getItem("user_id"),
        role:
          user?.role ??
          user?.userRole ??
          user?.userType ??
          user?.user_type ??
          localStorage.getItem("role") ??
          localStorage.getItem("userRole"),
      };
    }
  } catch (error) {
    console.error("Unable to read logged-in user:", error);
  }

  return {
    id:
      localStorage.getItem("userId") ||
      localStorage.getItem("user_id") ||
      localStorage.getItem("id") ||
      "",
    role:
      localStorage.getItem("role") ||
      localStorage.getItem("userRole") ||
      "",
  };
};

const getUserRole = (user) => {
  let role =
    user?.role ||
    user?.userRole ||
    user?.userType ||
    user?.user_type ||
    (Array.isArray(user?.roles) ? user.roles[0] : "");

  if (typeof role === "object" && role !== null) {
    role = role.name || role.role || role.authority || "";
  }

  return String(role)
    .replace(/^ROLE_/, "")
    .trim()
    .toUpperCase();
};

const normalizeUserType = (role) => {
  const normalized = String(role || "")
    .replace(/^ROLE_/, "")
    .trim()
    .toUpperCase();

  return normalized === "RECRUITER" ? "PARTNER" : normalized;
};

const getUserId = (user) => {
  const value =
    user?.id ??
    user?.userId ??
    user?.user_id ??
    localStorage.getItem("userId") ??
    localStorage.getItem("user_id") ??
    localStorage.getItem("id");

  const id = Number(value);

  return Number.isFinite(id) && id > 0 ? id : 0;
};

const formatDate = (value) => {
  if (!value) return "Not specified";

  const date = new Date(
    String(value).includes("T") ? value : `${value}T00:00:00`
  );

  if (Number.isNaN(date.getTime())) return "Not specified";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// =====================================================
// QUOTA HELPERS
// =====================================================

const getLimitValue = (usage) => {
  const value = Number(usage?.allowedLimit ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const getUsedValue = (usage) => {
  const value = Number(usage?.usedCount ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const isUnlimitedJobPosts = (usage) => getLimitValue(usage) === -1;

const getRemainingJobPosts = (usage) => {
  if (!usage) return 0;

  const limit = getLimitValue(usage);

  if (limit === -1) return "Unlimited";
  if (limit <= 0) return 0;

  const remaining = Number(usage.remainingCount);

  return Number.isFinite(remaining)
    ? Math.max(0, remaining)
    : Math.max(0, limit - getUsedValue(usage));
};

const hasJobPostAccess = (subscription, usage) => {
  if (
    String(subscription?.status || "").toUpperCase() !== "ACTIVE" ||
    !usage
  ) {
    return false;
  }

  const limit = getLimitValue(usage);
  const used = getUsedValue(usage);

  if (limit === -1) return true;
  if (limit <= 0) return false;

  return used < limit;
};

// =====================================================
// REUSABLE INPUT
// =====================================================

const InputField = ({
  label,
  name,
  placeholder,
  type = "text",
  required = false,
  icon: Icon,
  min,
  value,
  error,
  onChange,
}) => (
  <div className="field-group">
    <label htmlFor={name}>
      {Icon && <Icon size={16} />}
      {label}
      {required && <span className="required">*</span>}
    </label>

    <input
      id={name}
      name={name}
      type={type}
      value={value ?? ""}
      onChange={onChange}
      placeholder={placeholder}
      min={min}
      className={error ? "input-error" : ""}
      autoComplete="off"
    />

    {error && <div className="error-text">{error}</div>}
  </div>
);

// =====================================================
// MAIN COMPONENT
// =====================================================

const CreateJob = () => {
  const navigate = useNavigate();

  const loggedInUser = useMemo(() => getLoggedInUser(), []);
  const userRole = getUserRole(loggedInUser);
  const userId = getUserId(loggedInUser);
  const userType = normalizeUserType(userRole);

  const isAdmin = userRole === "ADMIN";

  const isAllowed =
    ["PARTNER", "RECRUITER", "ADMIN"].includes(userRole);

  const [job, setJob] = useState(INITIAL_JOB);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [skillInput, setSkillInput] = useState("");

  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);

  const [subscriptionLoading, setSubscriptionLoading] =
    useState(true);

  const [usageLoading, setUsageLoading] = useState(true);

  const [subscriptionError, setSubscriptionError] = useState("");
  const [refreshingSubscription, setRefreshingSubscription] =
    useState(false);

  // ===================================================
  // LOAD SUBSCRIPTION AND ACTUAL USAGE
  // ===================================================

  const loadActiveSubscription = useCallback(
    async (showRefresh = false) => {
      if (!userId) {
        setSubscription(null);
        setUsage(null);
        setSubscriptionError(
          "Unable to identify the logged-in user."
        );
        setSubscriptionLoading(false);
        setUsageLoading(false);
        return null;
      }

      if (showRefresh) {
        setRefreshingSubscription(true);
      } else {
        setSubscriptionLoading(true);
      }

      setUsageLoading(true);
      setSubscriptionError("");

      try {
        const activeSubscription = await getActiveSubscription(userId);

        setSubscription(activeSubscription || null);

        if (
          !activeSubscription ||
          String(activeSubscription.status || "").toUpperCase() !==
            "ACTIVE"
        ) {
          setUsage(null);
          return {
            subscription: activeSubscription || null,
            usage: null,
          };
        }

        const activeUsage = await getActiveFeatureUsage(
          userId,
          userType,
          "JOB_POST"
        );

        setUsage(activeUsage || null);

        if (!activeUsage) {
          setSubscriptionError(
            "No JOB_POST usage record was returned. Please contact the administrator."
          );
        }

        return {
          subscription: activeSubscription,
          usage: activeUsage || null,
        };
      } catch (error) {
        console.error("Subscription/quota load failed:", error);

        setSubscriptionError(
          error?.message ||
            "Unable to load your subscription quota. Please try again."
        );

        setSubscription(null);
        setUsage(null);

        return {
          subscription: null,
          usage: null,
          error,
        };
      } finally {
        setSubscriptionLoading(false);
        setUsageLoading(false);
        setRefreshingSubscription(false);
      }
    },
    [userId, userType]
  );

  // ===================================================
  // INITIAL LOAD AND ACCESS CONTROL
  // ===================================================

  useEffect(() => {
    if (!userId) {
      navigate("/login");
      return;
    }

    if (!isAllowed) {
      if (userRole === "TRAINER") {
        navigate("/trainer-dashboard");
      } else if (userRole === "EMPLOYEE") {
        navigate("/employee-dashboard");
      } else {
        navigate("/dashboard");
      }

      return;
    }

    if (!isAdmin) {
      loadActiveSubscription();
    } else {
      setSubscriptionLoading(false);
      setUsageLoading(false);
    }
  }, [
    userId,
    isAllowed,
    isAdmin,
    userRole,
    navigate,
    loadActiveSubscription,
  ]);

  // ===================================================
  // DERIVED SUBSCRIPTION VALUES
  // ===================================================

  const hasActivePlan =
    String(subscription?.status || "").toUpperCase() === "ACTIVE";

  const jobPostAllowed = hasJobPostAccess(subscription, usage);

  const remainingJobPosts = getRemainingJobPosts(usage);

  const quotaReady =
    isAdmin ||
    (!subscriptionLoading && !usageLoading && Boolean(usage));

  // ===================================================
  // FORM CHANGES
  // ===================================================

  const handleChange = (event) => {
    const { name, value } = event.target;

    setJob((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));
  };

  const skills = useMemo(
    () =>
      job.skillsRequired
        .split(",")
        .map((skill) => skill.trim())
        .filter(Boolean),
    [job.skillsRequired]
  );

  const addSkill = () => {
    const value = skillInput.trim();

    if (!value) return;

    const exists = skills.some(
      (skill) => skill.toLowerCase() === value.toLowerCase()
    );

    if (!exists) {
      setJob((previous) => ({
        ...previous,
        skillsRequired: [...skills, value].join(", "),
      }));

      setErrors((previous) => ({
        ...previous,
        skillsRequired: "",
      }));
    }

    setSkillInput("");
  };

  const removeSkill = (skillToRemove) => {
    setJob((previous) => ({
      ...previous,
      skillsRequired: skills
        .filter((skill) => skill !== skillToRemove)
        .join(", "),
    }));
  };

  const handleSkillKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addSkill();
    }
  };

  // ===================================================
  // VALIDATION
  // ===================================================

  const validate = () => {
    const nextErrors = {};

    if (!job.jobTitle.trim()) {
      nextErrors.jobTitle = "Job title is required";
    }

    if (!job.location.trim()) {
      nextErrors.location = "Location is required";
    }

    if (!job.skillsRequired.trim()) {
      nextErrors.skillsRequired =
        "Please add at least one skill";
    }

    if (!job.jobDescription.trim()) {
      nextErrors.jobDescription =
        "Job description is required";
    }

    if (
      job.salaryMin !== "" &&
      Number(job.salaryMin) < 0
    ) {
      nextErrors.salaryMin =
        "Minimum salary cannot be negative";
    }

    if (
      job.salaryMax !== "" &&
      Number(job.salaryMax) < 0
    ) {
      nextErrors.salaryMax =
        "Maximum salary cannot be negative";
    }

    if (
      job.salaryMin !== "" &&
      job.salaryMax !== "" &&
      Number(job.salaryMin) > Number(job.salaryMax)
    ) {
      nextErrors.salaryMax =
        "Maximum salary must be greater than or equal to minimum salary";
    }

    if (
      job.vacancy !== "" &&
      (!Number.isInteger(Number(job.vacancy)) ||
        Number(job.vacancy) < 1)
    ) {
      nextErrors.vacancy =
        "Vacancy must be a whole number of at least 1";
    }

    if (job.applicationDeadline) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const deadline = new Date(
        `${job.applicationDeadline}T00:00:00`
      );

      if (
        Number.isNaN(deadline.getTime()) ||
        deadline < today
      ) {
        nextErrors.applicationDeadline =
          "Application deadline cannot be in the past";
      }
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  // ===================================================
  // SUBSCRIPTION CHECK
  // ===================================================

  const checkJobPostSubscription = async () => {
    if (isAdmin) {
      return { allowed: true, usage: null };
    }

    // Always refresh the quota before posting.
    const result = await loadActiveSubscription(true);

    const currentSubscription = result?.subscription;
    const currentUsage = result?.usage;

    if (!currentSubscription) {
      const response = await Swal.fire({
        icon: "info",
        title: "Subscription Required",
        text:
          "You need an active subscription plan to post a job.",
        showCancelButton: true,
        confirmButtonText: "View Plans",
        cancelButtonText: "Cancel",
        confirmButtonColor: API_PRIMARY,
        reverseButtons: true,
      });

      if (response.isConfirmed) {
        navigate("/partner/subscription-plans");
      }

      return { allowed: false, usage: null };
    }

    if (
      String(currentSubscription.status || "").toUpperCase() !==
      "ACTIVE"
    ) {
      await Swal.fire({
        icon: "warning",
        title: "Subscription Not Active",
        text:
          "Please activate a subscription plan before posting a job.",
        confirmButtonColor: API_PRIMARY,
      });

      return { allowed: false, usage: currentUsage };
    }

    if (!currentUsage) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Verify Quota",
        text:
          "The job-post quota could not be loaded. Please refresh and try again.",
        confirmButtonColor: API_PRIMARY,
      });

      return { allowed: false, usage: null };
    }

    const limit = getLimitValue(currentUsage);
    const used = getUsedValue(currentUsage);

    if (limit === -1) {
      return { allowed: true, usage: currentUsage };
    }

    if (limit <= 0 || used >= limit) {
      const response = await Swal.fire({
        icon: "warning",
        title: "Job Post Limit Reached",
        html: `
          <div style="font-size:14px;line-height:1.6">
            You have used <strong>${used}</strong> out of
            <strong>${limit}</strong> job posts.
            <br/><br/>
            Please purchase a plan with an available job-post quota.
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "View Plans",
        cancelButtonText: "Cancel",
        confirmButtonColor: API_PRIMARY,
        reverseButtons: true,
      });

      if (response.isConfirmed) {
        navigate("/partner/subscription-plans");
      }

      return { allowed: false, usage: currentUsage };
    }

    return { allowed: true, usage: currentUsage };
  };

  // ===================================================
  // SUBMIT JOB
  // ===================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) return;

    if (!validate()) {
      await Swal.fire({
        icon: "warning",
        title: "Please check the form",
        text: "Some required information is missing or invalid.",
        confirmButtonColor: API_PRIMARY,
      });

      return;
    }

    if (!userId) {
      await Swal.fire({
        icon: "error",
        title: "Session expired",
        text: "Please login again.",
        confirmButtonColor: API_PRIMARY,
      });

      navigate("/login");
      return;
    }

    setLoading(true);

    try {
      const access = await checkJobPostSubscription();

      if (!access.allowed) return;

      const currentUsage = access.usage;
      const remaining = getRemainingJobPosts(currentUsage);

      const confirmation = await Swal.fire({
        icon: "question",
        title: "Publish this job?",
        html: `
          <div style="font-size:14px;line-height:1.6">
            Your job will be submitted for
            <strong>admin approval</strong>.
            <br/><br/>
            ${
              remaining === "Unlimited"
                ? "Remaining job posts: <strong>Unlimited</strong>"
                : `Remaining job posts after submission:
                   <strong>${Math.max(0, Number(remaining) - 1)}</strong>`
            }
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Submit Job",
        cancelButtonText: "Review Again",
        confirmButtonColor: API_PRIMARY,
        cancelButtonColor: "#6b7280",
        reverseButtons: true,
      });

      if (!confirmation.isConfirmed) return;

      const requestData = {
        postedBy: Number(userId),
        jobTitle: job.jobTitle.trim(),
        jobDescription: job.jobDescription.trim(),
        location: job.location.trim(),
        employeeType: job.employeeType || null,
        experienceRequired:
          job.experienceRequired.trim() || null,
        salaryMin:
          job.salaryMin !== "" ? Number(job.salaryMin) : null,
        salaryMax:
          job.salaryMax !== "" ? Number(job.salaryMax) : null,
        skillsRequired: job.skillsRequired.trim(),
        vacancy:
          job.vacancy !== "" ? Number(job.vacancy) : null,
        applicationDeadline: job.applicationDeadline
          ? `${job.applicationDeadline}T23:59:00`
          : null,
        status: "PENDING",
      };

      const response = await jobService.createJob(requestData);
      const responseData = response?.data ?? response;

      console.log("Job created:", responseData);

      // Backend increments usage. Refresh the database-backed quota.
      if (!isAdmin) {
        await loadActiveSubscription(true);
      }

      await Swal.fire({
        icon: "success",
        title: "Job submitted successfully!",
        html: `
          <div style="font-size:14px;line-height:1.6">
            Your job has been submitted for admin approval.
            <br/><br/>
            ${
              remaining === "Unlimited"
                ? "Your plan has unlimited job posts."
                : "Your job-post quota has been updated."
            }
          </div>
        `,
        confirmButtonColor: API_PRIMARY,
      });

      navigate("/partner/partner-jobs");
    } catch (error) {
      console.error("Create job error:", error);

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Unable to create the job. Please try again.";

      const lowerMessage = String(message).toLowerCase();

      const isSubscriptionError =
        lowerMessage.includes("subscription") ||
        lowerMessage.includes("job post limit") ||
        lowerMessage.includes("limit reached") ||
        lowerMessage.includes("quota");

      if (!isAdmin) {
        await loadActiveSubscription(true);
      }

      if (isSubscriptionError) {
        const result = await Swal.fire({
          icon: "warning",
          title: "Job Posting Limit",
          text: message,
          showCancelButton: true,
          confirmButtonText: "View Plans",
          cancelButtonText: "Close",
          confirmButtonColor: API_PRIMARY,
          reverseButtons: true,
        });

        if (result.isConfirmed) {
          navigate("/partner/subscription-plans");
        }

        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Unable to create job",
        text: message,
        confirmButtonColor: API_PRIMARY,
      });
    } finally {
      setLoading(false);
    }
  };

  // ===================================================
  // CANCEL
  // ===================================================

  const handleCancel = async () => {
    const hasData = Object.values(job).some(
      (value) => String(value).trim() !== ""
    );

    if (!hasData) {
      navigate("/partner/partner-jobs");
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Discard this job?",
      text: "Your entered information will be lost.",
      showCancelButton: true,
      confirmButtonText: "Discard",
      cancelButtonText: "Continue Editing",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      navigate("/partner/partner-jobs");
    }
  };

  if (!isAllowed) return null;

  const refreshing = refreshingSubscription || subscriptionLoading;

  // ===================================================
  // UI
  // ===================================================

  return (
    <div className="create-job-page">
      <PartnerHeader />

      <main className="main-container">
        <div className="breadcrumb">
          <button
            type="button"
            onClick={() => navigate("/partner/partner-jobs")}
          >
            My Jobs
          </button>
          <ChevronRight size={15} />
          <span>Create Job</span>
        </div>

        <div className="page-heading">
          <div className="heading-title">
            <div className="heading-icon">
              <BriefcaseBusiness size={27} />
            </div>
            <div>
              <h1>Create a New Job</h1>
              <p>
                Publish an opportunity and connect with qualified
                trainers and professionals.
              </p>
            </div>
          </div>

          <div>
            {subscriptionLoading && !isAdmin ? (
              <div className="status-badge neutral">
                <span className="small-spinner" />
                Checking subscription...
              </div>
            ) : isAdmin ? (
              <div className="status-badge admin">
                <CheckCircle2 size={16} /> Admin Access
              </div>
            ) : hasActivePlan && jobPostAllowed ? (
              <div className="status-badge active">
                <CheckCircle2 size={16} /> Active Plan
              </div>
            ) : (
              <button
                type="button"
                className="primary-button"
                onClick={() => navigate("/partner/subscription-plans")}
              >
                <CreditCard size={16} /> Buy Plan
              </button>
            )}
          </div>
        </div>

        {!isAdmin && (
          <section
            className={`subscription-banner ${
              hasActivePlan && jobPostAllowed ? "success" : "warning"
            }`}
          >
            <div className="subscription-info">
              <div className="subscription-icon">
                {hasActivePlan && jobPostAllowed ? (
                  <CheckCircle2 size={22} />
                ) : (
                  <AlertCircle size={22} />
                )}
              </div>

              <div>
                <strong>
                  {hasActivePlan
                    ? subscription?.planName || "Active Subscription"
                    : "Subscription required"}
                </strong>

                {subscriptionLoading || usageLoading ? (
                  <p>Loading job-post quota...</p>
                ) : usage ? (
                  <p>
                    Jobs used: <b>{getUsedValue(usage)}</b>
                    {" / "}
                    <b>
                      {isUnlimitedJobPosts(usage)
                        ? "Unlimited"
                        : getLimitValue(usage)}
                    </b>
                    {" · "}Remaining: <b>{remainingJobPosts}</b>
                  </p>
                ) : (
                  <p>
                    {subscriptionError ||
                      "Purchase an active plan with a job-post quota."}
                  </p>
                )}
              </div>
            </div>

            <div className="banner-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => loadActiveSubscription(true)}
                disabled={refreshing}
              >
                <RefreshCw
                  size={15}
                  className={refreshing ? "refresh-spin" : ""}
                />
                {refreshing ? "Checking..." : "Refresh"}
              </button>

              {(!hasActivePlan || !jobPostAllowed) && (
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => navigate("/partner/subscription-plans")}
                >
                  <CreditCard size={15} /> View Plans
                </button>
              )}
            </div>
          </section>
        )}

        {subscriptionError && !isAdmin && (
          <div className="subscription-error">
            <AlertCircle size={18} />
            <div>
              <strong>Subscription check failed</strong>
              <p>{subscriptionError}</p>
            </div>
            <button
              type="button"
              onClick={() => loadActiveSubscription(true)}
            >
              Try Again
            </button>
          </div>
        )}

        <div className="progress-card">
          {[
            ["1", "Job Details", "Basic information"],
            ["2", "Review", "Check your details"],
            ["3", "Publish", "Submit for approval"],
          ].map(([number, title, description], index) => (
            <React.Fragment key={number}>
              {index > 0 && <div className="progress-line" />}
              <div className={`progress-item ${index === 0 ? "active" : ""}`}>
                <div className="progress-number">{number}</div>
                <div>
                  <strong>{title}</strong>
                  <span>{description}</span>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="content-grid">
            <div className="form-column">
              <section className="form-card">
                <div className="section-header">
                  <div className="section-icon">
                    <BriefcaseBusiness size={19} />
                  </div>
                  <div>
                    <h2>Basic Job Information</h2>
                    <p>
                      Add the basic details candidates need to understand
                      the opportunity.
                    </p>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="full-width">
                    <InputField
                      label="Job Title"
                      name="jobTitle"
                      placeholder="e.g. Java Full Stack Trainer"
                      required
                      icon={BriefcaseBusiness}
                      value={job.jobTitle}
                      error={errors.jobTitle}
                      onChange={handleChange}
                    />
                  </div>

                  <InputField
                    label="Location"
                    name="location"
                    placeholder="e.g. Bangalore, Karnataka"
                    required
                    icon={MapPin}
                    value={job.location}
                    error={errors.location}
                    onChange={handleChange}
                  />

                  <div className="field-group">
                    <label htmlFor="employeeType">
                      <Users size={16} /> Employment Type
                    </label>
                    <select
                      id="employeeType"
                      name="employeeType"
                      value={job.employeeType}
                      onChange={handleChange}
                    >
                      <option value="">Select employment type</option>
                      <option value="FULL_TIME">Full Time</option>
                      <option value="PART_TIME">Part Time</option>
                      <option value="CONTRACT">Contract</option>
                      <option value="FREELANCE">Freelance</option>
                      <option value="INTERNSHIP">Internship</option>
                    </select>
                  </div>

                  <InputField
                    label="Experience Required"
                    name="experienceRequired"
                    placeholder="e.g. 3–5 Years"
                    icon={CheckCircle2}
                    value={job.experienceRequired}
                    error={errors.experienceRequired}
                    onChange={handleChange}
                  />

                  <InputField
                    label="Number of Vacancies"
                    name="vacancy"
                    placeholder="e.g. 2"
                    type="number"
                    min="1"
                    icon={Users}
                    value={job.vacancy}
                    error={errors.vacancy}
                    onChange={handleChange}
                  />

                  <InputField
                    label="Application Deadline"
                    name="applicationDeadline"
                    type="date"
                    icon={CalendarDays}
                    value={job.applicationDeadline}
                    error={errors.applicationDeadline}
                    onChange={handleChange}
                  />
                </div>
              </section>

              <section className="form-card">
                <div className="section-header">
                  <div className="section-icon">
                    <Plus size={19} />
                  </div>
                  <div>
                    <h2>Required Skills</h2>
                    <p>
                      Add technologies, tools, and skills required for
                      this position.
                    </p>
                  </div>
                </div>

                <div className="skill-input-row">
                  <input
                    value={skillInput}
                    onChange={(event) => setSkillInput(event.target.value)}
                    onKeyDown={handleSkillKeyDown}
                    placeholder="Type a skill and press Enter"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="add-skill-btn"
                    onClick={addSkill}
                  >
                    <Plus size={17} /> Add
                  </button>
                </div>

                {skills.length > 0 && (
                  <div className="skills-list">
                    {skills.map((skill, index) => (
                      <span
                        className="skill-chip"
                        key={`${skill}-${index}`}
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill(skill)}
                          aria-label={`Remove ${skill}`}
                        >
                          <X size={13} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {errors.skillsRequired && (
                  <div className="error-text">
                    {errors.skillsRequired}
                  </div>
                )}
              </section>

              <section className="form-card">
                <div className="section-header">
                  <div className="section-icon">
                    <IndianRupee size={19} />
                  </div>
                  <div>
                    <h2>Compensation</h2>
                    <p>
                      Optionally specify the salary or budget range.
                    </p>
                  </div>
                </div>

                <div className="salary-grid">
                  <InputField
                    label="Minimum Salary (₹)"
                    name="salaryMin"
                    type="number"
                    min="0"
                    placeholder="e.g. 30000"
                    value={job.salaryMin}
                    error={errors.salaryMin}
                    onChange={handleChange}
                  />
                  <InputField
                    label="Maximum Salary (₹)"
                    name="salaryMax"
                    type="number"
                    min="0"
                    placeholder="e.g. 60000"
                    value={job.salaryMax}
                    error={errors.salaryMax}
                    onChange={handleChange}
                  />
                </div>
              </section>

              <section className="form-card">
                <div className="section-header">
                  <div className="section-icon">
                    <FileText size={19} />
                  </div>
                  <div>
                    <h2>Job Description</h2>
                    <p>
                      Explain responsibilities, expectations, and
                      important requirements.
                    </p>
                  </div>
                </div>

                <div className="field-group">
                  <label htmlFor="jobDescription">
                    <FileText size={16} /> Description
                    <span className="required">*</span>
                  </label>
                  <textarea
                    id="jobDescription"
                    name="jobDescription"
                    rows="8"
                    value={job.jobDescription}
                    onChange={handleChange}
                    placeholder="Write a clear description of the role, responsibilities, requirements, and expectations..."
                    className={
                      errors.jobDescription ? "input-error" : ""
                    }
                  />
                  <div className="textarea-footer">
                    <span>{job.jobDescription.length} characters</span>
                    {errors.jobDescription && (
                      <span className="error-text">
                        {errors.jobDescription}
                      </span>
                    )}
                  </div>
                </div>
              </section>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={
                    loading ||
                    (!isAdmin &&
                      (!quotaReady ||
                        !hasActivePlan ||
                        !jobPostAllowed))
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner" /> Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} /> Submit Job
                    </>
                  )}
                </button>
              </div>

              {!isAdmin &&
                !subscriptionLoading &&
                !usageLoading &&
                (!hasActivePlan || !jobPostAllowed) && (
                  <div className="submit-disabled-note">
                    <AlertCircle size={15} />
                    <span>
                      {!subscription
                        ? "Purchase an active subscription to post a job."
                        : !usage
                          ? "The job-post quota could not be verified."
                          : "Your current plan does not have an available job-post quota."}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        navigate("/partner/subscription-plans")
                      }
                    >
                      View Plans
                    </button>
                  </div>
                )}
            </div>

            <aside className="preview-column">
              <div className="preview-card">
                <div className="preview-header">
                  <div>
                    <span className="preview-label">LIVE PREVIEW</span>
                    <h3>How candidates will see it</h3>
                  </div>
                  <div className="preview-dot" />
                </div>

                <div className="job-preview">
                  <div className="preview-company">
                    <div className="company-logo">C</div>
                    <div>
                      <strong>CertED Technologies</strong>
                      <span>Training &amp; EdTech</span>
                    </div>
                  </div>

                  <h2>{job.jobTitle.trim() || "Your Job Title"}</h2>

                  <div className="preview-meta">
                    <span>
                      <MapPin size={14} />
                      {job.location.trim() || "Location"}
                    </span>
                    {job.employeeType && (
                      <span>
                        <BriefcaseBusiness size={14} />
                        {job.employeeType
                          .replaceAll("_", " ")
                          .toLowerCase()
                          .replace(/\b\w/g, (letter) =>
                            letter.toUpperCase()
                          )}
                      </span>
                    )}
                  </div>

                  <div className="preview-divider" />

                  {[
                    ["Experience", job.experienceRequired || "Not specified"],
                    ["Vacancies", job.vacancy || "Not specified"],
                    [
                      "Salary",
                      job.salaryMin || job.salaryMax
                        ? `₹${job.salaryMin || "0"} - ₹${job.salaryMax || "0"}`
                        : "Not specified",
                    ],
                    ["Deadline", formatDate(job.applicationDeadline)],
                  ].map(([label, value]) => (
                    <div className="preview-detail" key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}

                  {skills.length > 0 && (
                    <>
                      <div className="preview-divider" />
                      <div className="preview-section-title">
                        Required Skills
                      </div>
                      <div className="preview-skills">
                        {skills.slice(0, 8).map((skill, index) => (
                          <span key={`${skill}-${index}`}>{skill}</span>
                        ))}
                        {skills.length > 8 && (
                          <span>+{skills.length - 8}</span>
                        )}
                      </div>
                    </>
                  )}

                  <div className="preview-divider" />
                  <div className="preview-section-title">About the Role</div>
                  <p className="preview-description">
                    {job.jobDescription.trim() ||
                      "Your job description will appear here as you type. Add responsibilities, requirements, and expectations for candidates."}
                  </p>
                </div>
              </div>

              <div className="tips-card">
                <div className="tips-title">
                  <CheckCircle2 size={18} />
                  Tips for a better job post
                </div>
                <ul>
                  <li>Use a clear and specific job title.</li>
                  <li>Mention all important technical skills.</li>
                  <li>Keep the description concise and practical.</li>
                  <li>Add salary information when possible.</li>
                </ul>
              </div>
            </aside>
          </div>
        </form>
      </main>

      <PartnerFooter />

      <style>{`
        * { box-sizing: border-box; }

        .create-job-page {
          min-height: 100vh;
          background: #f6f7fb;
          color: #1f2937;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }

        .main-container {
          max-width: 1450px;
          margin: auto;
          padding: 25px 28px 50px;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #777;
          margin-bottom: 18px;
        }

        .breadcrumb button {
          border: none;
          background: transparent;
          padding: 0;
          color: ${API_PRIMARY};
          font-weight: 600;
          cursor: pointer;
        }

        .page-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 18px;
        }

        .heading-title {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .heading-icon {
          width: 53px;
          height: 53px;
          border-radius: 14px;
          background: #eee8f8;
          color: ${API_PRIMARY};
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .page-heading h1 {
          margin: 0;
          font-size: 28px;
          color: #17121f;
        }

        .page-heading p {
          margin: 6px 0 0;
          color: #777;
          font-size: 14px;
        }

        .status-badge,
        .primary-button,
        .secondary-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          border-radius: 9px;
          padding: 10px 13px;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
        }

        .status-badge.active {
          background: #ecfdf3;
          color: #16834c;
          border: 1px solid #c9f1dc;
        }

        .status-badge.admin {
          background: #eee8f8;
          color: ${API_PRIMARY};
          border: 1px solid #ded2ef;
        }

        .status-badge.neutral {
          background: #f7f7f9;
          color: #777;
          border: 1px solid #e5e4e9;
        }

        .primary-button {
          border: 1px solid ${API_PRIMARY};
          background: ${API_PRIMARY};
          color: #fff;
          cursor: pointer;
        }

        .primary-button:hover,
        .submit-btn:hover {
          background: #321075;
        }

        .secondary-button {
          background: #fff;
          border: 1px solid #ddd9e4;
          color: #5d5862;
          cursor: pointer;
        }

        button:disabled {
          opacity: .65;
          cursor: not-allowed !important;
        }

        .subscription-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          padding: 14px 17px;
          border-radius: 12px;
          margin-bottom: 18px;
        }

        .subscription-banner.success {
          background: #f0fbf5;
          border: 1px solid #cceedd;
        }

        .subscription-banner.warning {
          background: #fffaf0;
          border: 1px solid #f1dfad;
        }

        .subscription-info {
          display: flex;
          align-items: center;
          gap: 11px;
        }

        .subscription-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: #dff6e9;
          color: #16834c;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .warning .subscription-icon {
          background: #fff0c9;
          color: #967300;
        }

        .subscription-info strong {
          display: block;
          font-size: 13px;
        }

        .subscription-info p {
          margin: 4px 0 0;
          font-size: 12px;
          color: #777;
          overflow-wrap: anywhere;
        }

        .banner-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .subscription-error {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #fff3f3;
          border: 1px solid #f2caca;
          color: #b42318;
          padding: 12px 15px;
          border-radius: 10px;
          margin-bottom: 18px;
        }

        .subscription-error > div { flex: 1; }
        .subscription-error strong { font-size: 12px; }
        .subscription-error p { margin: 3px 0 0; font-size: 11px; }

        .subscription-error button {
          border: 1px solid #e5bcbc;
          background: white;
          color: #b42318;
          border-radius: 7px;
          padding: 7px 10px;
          cursor: pointer;
        }

        .progress-card {
          background: #fff;
          border: 1px solid #e8e6ed;
          border-radius: 14px;
          padding: 15px 20px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          max-width: 850px;
        }

        .progress-item {
          display: flex;
          align-items: center;
          gap: 9px;
          min-width: 130px;
        }

        .progress-number {
          width: 29px;
          height: 29px;
          border-radius: 50%;
          background: #eeeeF3;
          color: #777;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          flex-shrink: 0;
        }

        .progress-item.active .progress-number {
          background: ${API_PRIMARY};
          color: white;
        }

        .progress-item div:last-child {
          display: flex;
          flex-direction: column;
        }

        .progress-item strong { font-size: 12px; }
        .progress-item span { font-size: 10px; color: #999; margin-top: 2px; }

        .progress-line {
          height: 1px;
          background: #e5e4e9;
          flex: 1;
          margin: 0 12px;
        }

        .content-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 390px;
          gap: 22px;
          align-items: start;
        }

        .form-column {
          display: flex;
          flex-direction: column;
          gap: 18px;
          min-width: 0;
        }

        .form-card,
        .preview-card,
        .tips-card {
          background: #fff;
          border: 1px solid #e7e5eb;
          border-radius: 15px;
          box-shadow: 0 4px 18px rgba(30, 20, 50, .025);
        }

        .form-card { padding: 23px; }

        .section-header {
          display: flex;
          align-items: flex-start;
          gap: 11px;
          margin-bottom: 22px;
        }

        .section-icon {
          flex-shrink: 0;
          width: 37px;
          height: 37px;
          border-radius: 9px;
          background: #f0ebf8;
          color: ${API_PRIMARY};
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .section-header h2 {
          margin: 1px 0 4px;
          font-size: 16px;
          color: #28212f;
        }

        .section-header p {
          margin: 0;
          font-size: 12px;
          color: #8a8790;
          line-height: 1.5;
        }

        .form-grid,
        .salary-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 17px;
        }

        .full-width { grid-column: 1 / -1; }

        .field-group { display: flex; flex-direction: column; min-width: 0; }

        .field-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 700;
          color: #48424e;
          margin-bottom: 7px;
        }

        .field-group label svg { color: ${API_SECONDARY}; }
        .required { color: #dc2626; }

        .field-group input,
        .field-group select,
        .field-group textarea,
        .skill-input-row input {
          width: 100%;
          border: 1px solid #ddd9e3;
          background: #fff;
          border-radius: 9px;
          outline: none;
          color: #29242f;
          font-size: 13px;
          transition: border-color .2s, box-shadow .2s;
        }

        .field-group input,
        .field-group select {
          height: 43px;
          padding: 0 12px;
        }

        .field-group textarea {
          padding: 12px;
          resize: vertical;
          line-height: 1.55;
        }

        .field-group input:focus,
        .field-group select:focus,
        .field-group textarea:focus,
        .skill-input-row input:focus {
          border-color: ${API_SECONDARY};
          box-shadow: 0 0 0 3px rgba(149, 100, 221, .1);
        }

        .input-error { border-color: #dc2626 !important; }
        .error-text { color: #dc2626; font-size: 11px; margin-top: 5px; }

        .skill-input-row { display: flex; gap: 9px; }

        .skill-input-row input { height: 43px; padding: 0 12px; }

        .add-skill-btn {
          height: 43px;
          padding: 0 17px;
          border: none;
          border-radius: 9px;
          background: #eee8f8;
          color: ${API_PRIMARY};
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
        }

        .skills-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 13px;
        }

        .skill-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: #f1ebf9;
          color: ${API_PRIMARY};
          border: 1px solid #e3d7f3;
          border-radius: 30px;
          padding: 7px 9px 7px 11px;
          font-size: 11px;
          font-weight: 700;
        }

        .skill-chip button {
          border: none;
          background: #ddd0ef;
          color: ${API_PRIMARY};
          width: 17px;
          height: 17px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
        }

        .textarea-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          min-height: 18px;
          font-size: 10px;
          color: #999;
        }

        .textarea-footer .error-text { margin: 0; }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 5px 0 0;
        }

        .cancel-btn,
        .submit-btn {
          min-height: 46px;
          padding: 0 21px;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .cancel-btn {
          background: white;
          border: 1px solid #dcd9e2;
          color: #5d5862;
        }

        .submit-btn {
          background: ${API_PRIMARY};
          border: 1px solid ${API_PRIMARY};
          color: white;
          min-width: 145px;
        }

        .submit-disabled-note {
          display: flex;
          align-items: center;
          gap: 7px;
          background: #fff9e8;
          border: 1px solid #f0e0a9;
          color: #806d16;
          padding: 10px 12px;
          border-radius: 9px;
          font-size: 11px;
        }

        .submit-disabled-note span { flex: 1; }

        .submit-disabled-note button {
          border: none;
          background: transparent;
          color: ${API_PRIMARY};
          font-weight: 800;
          cursor: pointer;
          font-size: 11px;
        }

        .spinner,
        .small-spinner {
          width: 15px;
          height: 15px;
          border: 2px solid rgba(100, 100, 100, .25);
          border-top-color: ${API_PRIMARY};
          border-radius: 50%;
          animation: spin .7s linear infinite;
        }

        .spinner {
          border-color: rgba(255,255,255,.35);
          border-top-color: white;
        }

        .refresh-spin { animation: spin .7s linear infinite; }

        @keyframes spin { to { transform: rotate(360deg); } }

        .preview-column {
          position: sticky;
          top: 92px;
          display: flex;
          flex-direction: column;
          gap: 15px;
          min-width: 0;
        }

        .preview-card { overflow: hidden; }

        .preview-header {
          padding: 18px;
          background: linear-gradient(135deg, #faf8fd, #f5f1fa);
          border-bottom: 1px solid #e9e3f0;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .preview-label {
          font-size: 9px;
          font-weight: 800;
          color: ${API_SECONDARY};
          letter-spacing: 1px;
        }

        .preview-header h3 { margin: 4px 0 0; font-size: 14px; color: #312936; }

        .preview-dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #35a854;
          box-shadow: 0 0 0 4px rgba(53,168,84,.1);
        }

        .job-preview { padding: 20px; }

        .preview-company {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 19px;
        }

        .company-logo {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          background: ${API_PRIMARY};
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
        }

        .preview-company div:last-child { display: flex; flex-direction: column; }
        .preview-company strong { font-size: 11px; color: #35303b; }
        .preview-company span { font-size: 9px; color: #999; margin-top: 2px; }

        .job-preview > h2 {
          margin: 0 0 12px;
          font-size: 19px;
          line-height: 1.35;
          color: #221c27;
          overflow-wrap: anywhere;
        }

        .preview-meta { display: flex; flex-direction: column; gap: 7px; }

        .preview-meta span {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #716c75;
        }

        .preview-divider { height: 1px; background: #eeeaf0; margin: 16px 0; }

        .preview-detail {
          display: flex;
          justify-content: space-between;
          gap: 15px;
          padding: 6px 0;
        }

        .preview-detail span { color: #999; font-size: 10px; }
        .preview-detail strong { color: #443e48; font-size: 10px; text-align: right; }

        .preview-section-title {
          font-size: 10px;
          font-weight: 800;
          color: #4b4550;
          margin-bottom: 8px;
        }

        .preview-skills { display: flex; flex-wrap: wrap; gap: 5px; }

        .preview-skills span {
          background: #f2edf8;
          color: ${API_PRIMARY};
          border-radius: 5px;
          padding: 5px 7px;
          font-size: 9px;
          font-weight: 700;
        }

        .preview-description {
          margin: 0;
          color: #77727b;
          font-size: 10px;
          line-height: 1.6;
          overflow-wrap: anywhere;
          white-space: pre-wrap;
        }

        .tips-card { padding: 17px; }

        .tips-title {
          display: flex;
          align-items: center;
          gap: 7px;
          color: ${API_PRIMARY};
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 9px;
        }

        .tips-card ul { margin: 0; padding-left: 18px; }
        .tips-card li { color: #777; font-size: 10px; line-height: 1.6; margin: 4px 0; }

        @media (max-width: 1100px) {
          .content-grid { grid-template-columns: minmax(0, 1fr) 320px; }
        }

        @media (max-width: 850px) {
          .content-grid { grid-template-columns: 1fr; }
          .preview-column { position: static; order: -1; }
          .preview-card, .tips-card { display: none; }
          .page-heading { align-items: flex-start; flex-direction: column; }
          .progress-card { max-width: none; }
          .subscription-banner { align-items: flex-start; flex-direction: column; }
          .banner-actions { width: 100%; }
          .banner-actions button { flex: 1; }
        }

        @media (max-width: 650px) {
          .main-container { padding: 18px 13px 35px; }
          .page-heading h1 { font-size: 22px; }
          .page-heading p { font-size: 12px; }
          .progress-card { overflow-x: auto; padding: 13px; }
          .progress-item { min-width: 125px; }
          .progress-line { min-width: 20px; margin: 0 7px; }
          .form-card { padding: 17px; }
          .form-grid, .salary-grid { grid-template-columns: 1fr; }
          .full-width { grid-column: auto; }
          .skill-input-row { flex-direction: column; }
          .add-skill-btn { justify-content: center; }
          .form-actions {
            position: sticky;
            bottom: 0;
            z-index: 20;
            background: #f6f7fb;
            padding: 12px 0;
          }
          .cancel-btn, .submit-btn { flex: 1; }
          .subscription-info { align-items: flex-start; }
          .banner-actions { flex-direction: column; }
          .banner-actions button { width: 100%; }
          .subscription-error { align-items: flex-start; flex-wrap: wrap; }
        }
      `}</style>
    </div>
  );
};

export default CreateJob;
